import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { type ComponentProps, type ElementRef, forwardRef } from 'react';

import { cx } from '../../utils/cva';

export type ScrollAreaViewportProps = ComponentProps<
  typeof ScrollAreaPrimitive.Viewport
>;

export type ScrollAreaCornerProps = ComponentProps<
  typeof ScrollAreaPrimitive.Corner
>;

export const ScrollAreaCorner = ScrollAreaPrimitive.Corner;

export type ScrollAreaRootProps = ComponentProps<
  typeof ScrollAreaPrimitive.Root
>;

export const ScrollAreaRoot = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaRootProps
>((props, forwardedRef) => {
  const { className, ...rest } = props;
  return (
    <ScrollAreaPrimitive.Root
      ref={forwardedRef}
      className={cx('flex-auto', 'min-h-0', 'flex', 'flex-col', className)}
      {...rest}
    />
  );
});

export const ScrollAreaViewport = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.Viewport>,
  ScrollAreaViewportProps
>((props, forwardedRef) => {
  const { className, ...rest } = props;
  return (
    <ScrollAreaPrimitive.Viewport
      ref={forwardedRef}
      className={cx(
        'flex-auto',
        'min-h-0',
        'flex-col',
        '[&>div]:!block',
        className,
      )}
      {...rest}
    />
  );
});

export type ScrollAreaScrollbarProps = ComponentProps<
  typeof ScrollAreaPrimitive.Scrollbar
>;

export const ScrollAreaScrollbar = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.Scrollbar>,
  ScrollAreaScrollbarProps
>((props, forwardedRef) => {
  const { className, ...rest } = props;
  return (
    <ScrollAreaPrimitive.Scrollbar
      ref={forwardedRef}
      className={cx(
        'flex',
        'select-none',
        'touch-none',
        'p-0.5',
        'data-[orientation=vertical]:w-3',
        'data-[orientation=horizontal]:flex-col',
        'data-[orientation=horizontal]:h-3',
        className,
      )}
      {...rest}
    />
  );
});

export type ScrollAreaThumbProps = ComponentProps<
  typeof ScrollAreaPrimitive.Thumb
>;

export const ScrollAreaThumb = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.Thumb>,
  ScrollAreaThumbProps
>((props, forwardedRef) => {
  const { className, ...rest } = props;
  return (
    <ScrollAreaPrimitive.Thumb
      ref={forwardedRef}
      className={cx(
        'flex-1',
        'bg-alpha-10',
        'transition-colors',
        'hover:bg-alpha-20',
        'rounded-full',
        'relative',
        'before:content-[""]',
        'before:absolute',
        'before:top-1/2',
        'before:left-1/2',
        'before:-translate-x-1/2',
        'before:-translate-y-1/2',
        'before:w-full',
        'before:h-full',
        'before:min-w-[44px]',
        'before:min-h-[44px]',
        className,
      )}
      {...rest}
    />
  );
});
